import { useMemo, useState } from 'react';
import type { BoardSummary, CalendarEvent, Organization } from '../lib/api';
import type { CalendarTaskDue } from '../lib/lmsApi';
import { Icon } from '../ui/icons';
import { plural } from './BoardsView';
import {
  WEEKDAY_LABELS,
  eventOnDay,
  formatDayLong,
  formatRelative,
  formatRelativeDay,
  formatTime,
  isSameDay,
  isToday,
  isWeekend,
  monthGrid,
  monthTitle,
  startOfDay,
} from './dates';
import type { RemindersApi } from './useReminders';

/** Chips shown per day cell before collapsing into a counter. */
const MAX_CHIPS = 3;

/** What a chip carries while it is being dragged to another day. */
const DRAG_MIME = 'application/x-kanvas-calendar';

interface DragPayload {
  kind: 'event' | 'task';
  id: string;
}

export interface CalendarViewProps {
  org: Organization;
  events: CalendarEvent[];
  boards: BoardSummary[];
  /**
   * Task deadlines shown alongside events. A student's homework is a date in
   * the same calendar they check for meetings, so it belongs here rather than
   * only inside «Мои задания».
   */
  taskDues: CalendarTaskDue[];
  onOpenTask: (taskId: string) => void;
  loading: boolean;
  month: Date;
  selectedDay: Date;
  reminders: RemindersApi;
  /** A student reads the calendar; teachers and the owner also plan in it. */
  canCreateEvent: boolean;
  canEditEvent: (event: CalendarEvent) => boolean;
  viewerIsStudent: boolean;
  onMonthChange: (month: Date) => void;
  onSelectDay: (day: Date) => void;
  /** Click on a day: the detail window the grid has no room for. */
  onOpenDay: (day: Date) => void;
  onCreateEvent: (day: Date) => void;
  onEditEvent: (event: CalendarEvent) => void;
  onToggleTask: (event: CalendarEvent, taskId: string, done: boolean) => Promise<void>;
  onToggleEventDone: (event: CalendarEvent, done: boolean) => void;
  onToggleHomeworkStudent: (taskId: string, studentId: string, done: boolean) => void;
  viewerUserId: string | null;
  canManageHomework: boolean;
  /** Drag-and-drop reschedule; both keep their time of day. */
  onMoveEvent: (eventId: string, day: Date) => Promise<void>;
  onMoveTaskDue: (taskId: string, day: Date) => Promise<void>;
  onOpenBoard: (id: string) => void;
}

export function CalendarView({
  org,
  events,
  boards,
  taskDues,
  onOpenTask,
  loading,
  month,
  selectedDay,
  reminders,
  canCreateEvent,
  canEditEvent,
  viewerIsStudent,
  onMonthChange,
  onSelectDay,
  onOpenDay,
  onCreateEvent,
  onEditEvent,
  onToggleTask,
  onToggleEventDone,
  onToggleHomeworkStudent,
  viewerUserId,
  canManageHomework,
  onMoveEvent,
  onMoveTaskDue,
  onOpenBoard,
}: CalendarViewProps) {
  const days = useMemo(() => monthGrid(month), [month]);
  /** Day cell currently under a dragged chip, so the drop target is obvious. */
  const [dropDay, setDropDay] = useState<string | null>(null);

  /**
   * Events bucketed by day, built once per render instead of filtering the
   * whole list inside each of the 42 cells.
   */
  const byDay = useMemo(() => {
    const map = new Map<string, CalendarEvent[]>();
    for (const day of days) {
      const key = day.toDateString();
      const onThisDay = events.filter((event) => eventOnDay(event, day));
      if (onThisDay.length > 0) map.set(key, onThisDay);
    }
    return map;
  }, [days, events]);

  const dayEvents = useMemo(
    () => events.filter((event) => eventOnDay(event, selectedDay)),
    [events, selectedDay],
  );

  const duesByDay = useMemo(() => {
    const map = new Map<string, CalendarTaskDue[]>();
    for (const due of taskDues) {
      const key = new Date(due.dueAt).toDateString();
      const list = map.get(key);
      if (list) list.push(due);
      else map.set(key, [due]);
    }
    return map;
  }, [taskDues]);

  const dayDues = duesByDay.get(selectedDay.toDateString()) ?? [];

  const upcoming = useMemo(() => {
    const now = Date.now();
    return events
      .filter((event) => !event.done && new Date(event.end).getTime() >= now)
      .slice(0, 6);
  }, [events]);

  const boardName = (id: string | null) =>
    id ? (boards.find((board) => board.id === id)?.name ?? null) : null;

  /** Who a deadline is for, in as few characters as a chip can hold. */
  const dueWho = (due: CalendarTaskDue) => {
    if (viewerIsStudent) return null;
    if (due.students.length === 0) return null;
    if (due.students.length === 1) return due.students[0]!.name;
    return `${due.students[0]!.name} +${due.students.length - 1}`;
  };

  const startDrag = (e: React.DragEvent, payload: DragPayload) => {
    e.stopPropagation();
    e.dataTransfer.setData(DRAG_MIME, JSON.stringify(payload));
    e.dataTransfer.effectAllowed = 'move';
  };

  const drop = (e: React.DragEvent, day: Date) => {
    e.preventDefault();
    setDropDay(null);
    const raw = e.dataTransfer.getData(DRAG_MIME);
    if (!raw) return;
    try {
      const payload = JSON.parse(raw) as DragPayload;
      if (payload.kind === 'event') void onMoveEvent(payload.id, day);
      else void onMoveTaskDue(payload.id, day);
    } catch {
      // A drop from somewhere else entirely; nothing to reschedule.
    }
  };

  return (
    <section className="dash-content">
      <header className="dash-header">
        <div>
          <h1>Календарь</h1>
          <p className="dash-subtitle">
            {org.name} ·{' '}
            {loading
              ? 'загрузка…'
              : `${events.length} ${plural(events.length, 'событие', 'события', 'событий')}${
                  taskDues.length > 0
                    ? `, ${taskDues.length} ${plural(taskDues.length, 'задание', 'задания', 'заданий')}`
                    : ''
                }`}
          </p>
        </div>

        <div className="cal-actions">
          {reminders.permission === 'default' && (
            <button
              type="button"
              className="ghost-btn"
              onClick={() => void reminders.requestPermission()}
            >
              <Icon name="bell" size={15} />
              Включить напоминания
            </button>
          )}
          {reminders.permission === 'denied' && (
            <span className="cal-hint" title="Разрешение на уведомления отклонено в браузере">
              <Icon name="bell" size={14} />
              Уведомления запрещены — напоминания покажутся в приложении
            </span>
          )}
          {canCreateEvent && (
            <button
              type="button"
              className="primary-btn"
              onClick={() => onCreateEvent(selectedDay)}
            >
              <Icon name="plus" size={16} />
              Новое событие
            </button>
          )}
        </div>
      </header>

      <div className="cal-layout">
        <div className="cal-main">
          <div className="cal-toolbar">
            <button
              type="button"
              className="icon-btn"
              aria-label="Предыдущий месяц"
              onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() - 1, 1))}
            >
              <Icon name="chevronDown" size={16} className="rotate-90" />
            </button>
            <h2 className="cal-month">{monthTitle(month)}</h2>
            <button
              type="button"
              className="icon-btn"
              aria-label="Следующий месяц"
              onClick={() => onMonthChange(new Date(month.getFullYear(), month.getMonth() + 1, 1))}
            >
              <Icon name="chevronDown" size={16} className="rotate-270" />
            </button>
            <button
              type="button"
              className="ghost-btn"
              onClick={() => {
                const now = new Date();
                onMonthChange(new Date(now.getFullYear(), now.getMonth(), 1));
                onSelectDay(startOfDay(now));
              }}
            >
              Сегодня
            </button>
          </div>

          <div className="cal-weekdays" aria-hidden="true">
            {WEEKDAY_LABELS.map((label) => (
              <span key={label}>{label}</span>
            ))}
          </div>

          {/* `dragend` bubbles from the chip that was picked up, so one
              handler here clears the highlight however the drag ended —
              including a drop outside the grid, which fires no `drop`. */}
          <div className="cal-grid" role="grid" onDragEnd={() => setDropDay(null)}>
            {days.map((day) => {
              const key = day.toDateString();
              const list = byDay.get(key) ?? [];
              const dues = duesByDay.get(key) ?? [];
              const outside = day.getMonth() !== month.getMonth();
              const selected = isSameDay(day, selectedDay);

              return (
                <button
                  key={day.toISOString()}
                  type="button"
                  role="gridcell"
                  aria-selected={selected}
                  className={[
                    'cal-day',
                    outside && 'is-outside',
                    isToday(day) && 'is-today',
                    isWeekend(day) && 'is-weekend',
                    selected && 'is-selected',
                    dropDay === key && 'is-drop-target',
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => {
                    onSelectDay(startOfDay(day));
                    onOpenDay(startOfDay(day));
                  }}
                  onDragOver={(e) => {
                    if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
                    e.preventDefault();
                    e.dataTransfer.dropEffect = 'move';
                    setDropDay(key);
                  }}
                  onDragLeave={(e) => {
                    // `dragleave` bubbles, so moving the pointer from the cell
                    // onto one of its own chips fires it too. Without this the
                    // highlight blinks off and on as the cursor crosses them.
                    if (e.currentTarget.contains(e.relatedTarget as Node | null)) return;
                    setDropDay((current) => (current === key ? null : current));
                  }}
                  onDrop={(e) => drop(e, startOfDay(day))}
                >
                  <span className="cal-day-number">{day.getDate()}</span>

                  <span className="cal-day-events">
                    {list.slice(0, MAX_CHIPS).map((event) => {
                      const movable = canEditEvent(event);
                      return (
                        <span
                          key={event.id}
                          className={`cal-chip ${movable ? 'is-movable' : ''} ${event.done ? 'is-done' : ''}`}
                          style={{ borderLeftColor: event.color }}
                          title={event.title}
                          draggable={movable}
                          onDragStart={(e) => startDrag(e, { kind: 'event', id: event.id })}
                        >
                          {!event.allDay && (
                            <em className="cal-chip-time">{formatTime(event.start)}</em>
                          )}
                          {event.title}
                        </span>
                      );
                    })}
                    {list.length > MAX_CHIPS && (
                      <span className="cal-chip is-more">ещё {list.length - MAX_CHIPS}</span>
                    )}

                    {dues.slice(0, MAX_CHIPS).map((due) => {
                      const who = dueWho(due);
                      return (
                        <span
                          key={due.id}
                          className={[
                            'cal-chip',
                            'is-task',
                            due.done === true && 'is-done',
                            due.awaitingReview && 'is-review',
                            due.mayReschedule && 'is-movable',
                          ]
                            .filter(Boolean)
                            .join(' ')}
                          title={`${due.subject ? `${due.subject}: ` : ''}${due.title}${
                            who ? ` — ${who}` : ''
                          }`}
                          draggable={due.mayReschedule}
                          onDragStart={(e) => startDrag(e, { kind: 'task', id: due.id })}
                        >
                          <em className="cal-chip-time">
                            <Icon name="clipboard" size={10} />
                          </em>
                          {due.subject ? `${due.subject}: ${due.title}` : due.title}
                          {who && <em className="cal-chip-who">{who}</em>}
                        </span>
                      );
                    })}
                    {dues.length > MAX_CHIPS && (
                      <span className="cal-chip is-more">
                        ещё {dues.length - MAX_CHIPS} заданий
                      </span>
                    )}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <aside className="cal-side">
          <div className="cal-day-panel">
            <header>
              <h3>{formatRelativeDay(selectedDay)}</h3>
              <p>{formatDayLong(selectedDay)}</p>
            </header>

            {dayDues.length > 0 && (
              <div className="cal-task-dues">
                <p className="popover-label">Задания</p>
                <ul>
                  {dayDues.map((due) => (
                    <li key={due.id} className={due.done ? 'is-complete' : due.awaitingReview ? 'is-review' : ''}>
                      {(viewerIsStudent && viewerUserId) || canManageHomework ? (
                        <label className="day-done-check">
                          <input
                            type="checkbox"
                            checked={
                              viewerIsStudent
                                ? due.students.find((s) => s.userId === viewerUserId)?.done === true
                                : due.students.length > 0 && due.students.every((s) => s.done)
                            }
                            aria-label="Отметить задание"
                            onChange={(e) => {
                              const next = e.target.checked;
                              if (viewerIsStudent && viewerUserId) {
                                onToggleHomeworkStudent(due.id, viewerUserId, next);
                                return;
                              }
                              for (const student of due.students) {
                                if (student.done !== next) {
                                  onToggleHomeworkStudent(due.id, student.userId, next);
                                }
                              }
                            }}
                          />
                        </label>
                      ) : null}
                      <button type="button" onClick={() => onOpenTask(due.id)}>
                        <Icon name="clipboard" size={13} />
                        <span>
                          {due.subject && <span className="subject-badge">{due.subject}</span>}
                          {due.title}
                          {dueWho(due) && <em className="cal-side-who">{dueWho(due)}</em>}
                        </span>
                        <em>{formatTime(due.dueAt)}</em>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {dayEvents.length === 0 ? (
              <p className="cal-empty">
                Ничего не запланировано.
                {canCreateEvent && (
                  <button type="button" onClick={() => onCreateEvent(selectedDay)}>
                    Добавить событие
                  </button>
                )}
              </p>
            ) : (
              <ul className="cal-event-list">
                {dayEvents.map((event) => {
                  const linkedBoard = boardName(event.boardId);
                  const doneCount = event.tasks.filter((task) => task.done).length;
                  const editable = canEditEvent(event);

                  return (
                    <li
                      key={event.id}
                      className={`cal-event ${event.done ? 'is-complete' : ''}`}
                      style={{ borderLeftColor: event.color }}
                    >
                      <div className="cal-event-head">
                        {editable && (
                          <label className="day-done-check">
                            <input
                              type="checkbox"
                              checked={event.done}
                              aria-label={event.done ? 'Снять отметку о выполнении' : 'Отметить событие выполненным'}
                              onChange={(e) => onToggleEventDone(event, e.target.checked)}
                            />
                          </label>
                        )}
                        {editable ? (
                          <button
                            type="button"
                            className={`cal-event-title ${event.done ? 'is-done' : ''}`}
                            onClick={() => onEditEvent(event)}
                          >
                            {event.title}
                          </button>
                        ) : (
                          <span className={`cal-event-title is-static ${event.done ? 'is-done' : ''}`}>{event.title}</span>
                        )}
                        <span className="cal-event-time">
                          {event.allDay
                            ? 'весь день'
                            : `${formatTime(event.start)}–${formatTime(event.end)}`}
                        </span>
                      </div>

                      {event.remindMinutesBefore !== null && (
                        <p className="cal-event-remind">
                          <Icon name="bell" size={12} />
                          {event.remindMinutesBefore === 0
                            ? 'в момент начала'
                            : `за ${event.remindMinutesBefore} мин`}
                        </p>
                      )}

                      {event.description && <p className="cal-event-desc">{event.description}</p>}

                      {linkedBoard && (
                        <button
                          type="button"
                          className="cal-event-board"
                          onClick={() => event.boardId && onOpenBoard(event.boardId)}
                        >
                          <Icon name="boards" size={13} />
                          {linkedBoard}
                        </button>
                      )}

                      {event.tasks.length > 0 && (
                        <div className="cal-event-tasks">
                          <p className="cal-task-progress">
                            Задачи: {doneCount} / {event.tasks.length}
                          </p>
                          <ul>
                            {event.tasks.map((task) => (
                              <li key={task.id}>
                                <label>
                                  <input
                                    type="checkbox"
                                    checked={task.done}
                                    disabled={!editable}
                                    onChange={(e) =>
                                      void onToggleTask(event, task.id, e.target.checked)
                                    }
                                  />
                                  <span className={task.done ? 'is-done' : ''}>{task.text}</span>
                                </label>
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>

          <div className="cal-upcoming">
            <h3>
              <Icon name="clock" size={14} />
              Ближайшие
            </h3>
            {upcoming.length === 0 ? (
              <p className="cal-empty">Пока ничего не запланировано.</p>
            ) : (
              <ul>
                {upcoming.map((event) => (
                  <li key={event.id}>
                    <button
                      type="button"
                      onClick={() =>
                        canEditEvent(event)
                          ? onEditEvent(event)
                          : onOpenDay(startOfDay(new Date(event.start)))
                      }
                    >
                      <span className="cal-dot" style={{ background: event.color }} />
                      <span className="cal-upcoming-text">
                        <strong>{event.title}</strong>
                        <em>{formatRelative(event.start)}</em>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}
